import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 },
  }),
};

const MEMBERS = [
  {
    name: "AJ Casavant",
    role: "Managing Principal",
    tag: "Builder · Financier · Preservationist",
    image: "/team/aj-casavant.jpg",
    blurb:
      "Leads acquisitions, development, and capital strategy — with more than $1.1B in project experience across affordable and mixed-income housing.",
    to: "/team/aj-casavant",
    testid: "aj",
  },
  {
    name: "Kristen Hand",
    role: "Chief Financial Officer",
    tag: "Finance · Capital · Portfolio Strategy",
    image: "/team/kristen-hand.jpg",
    blurb:
      "Leads finance and capital operations — overseeing structured finance and reporting across the portfolio's acquisition and preservation pipeline.",
  },
  {
    name: "Priyanshi Bhatnagar",
    role: "Analyst",
    tag: "Underwriting · Research",
    image: "/team/priyanshi-bhatnagar.jpg",
    blurb:
      "Leads underwriting, market research, and portfolio analysis behind every acquisition and preservation decision, with a background in chartered financial analysis.",
  },
];

export const Team = () => {
  return (
    <section id="team" data-testid="team-section" className="relative bg-[#F7F5F0] py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-y-8 md:gap-12 items-end mb-16 md:mb-24">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C56A4E] mb-6">Executive Team</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1C2220]">
              An experienced team with a <span className="italic text-[#C56A4E]">long-term</span> perspective.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-base leading-relaxed text-[#1C2220]/70">
              We are intentionally lean. Every deal is touched by the people whose names
              are on it — from underwriting through long-term stewardship.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {MEMBERS.map((m, i) => {
            const Card = (
              <motion.div
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                data-testid={`team-member-${i}`}
                className="group h-full"
              >
                {/* Portrait or monogram */}
                <div className="overflow-hidden rounded-sm aspect-[4/5] mb-6 relative">
                  {m.image ? (
                    <img
                      src={m.image}
                      alt={m.name}
                      className="h-full w-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-[filter,transform] duration-1000 ease-out"
                    />
                  ) : (
                    <div className="h-full w-full bg-[#1C2220] flex items-center justify-center relative overflow-hidden">
                      <span className="font-heading text-7xl md:text-8xl text-[#E8B4A0] group-hover:scale-105 transition-transform duration-700">
                        {m.monogram}
                      </span>
                      <span className="absolute bottom-4 right-4 h-16 w-16 rounded-full border border-[#C56A4E]/40" />
                    </div>
                  )}
                  {m.to && (
                    <span className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/90 text-[#1C2220] flex items-center justify-center opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                      <ArrowUpRight size={18} />
                    </span>
                  )}
                </div>

                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-heading text-2xl md:text-3xl text-[#1C2220] tracking-tight">{m.name}</h3>
                </div>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#C56A4E]">{m.role}</p>
                <p className="mt-3 text-sm font-heading italic text-[#1C2220]/60">{m.tag}</p>
                <p className="mt-4 text-base leading-relaxed text-[#1C2220]/70">{m.blurb}</p>
                {m.to && (
                  <span
                    data-testid="aj-read-bio"
                    className="mt-5 inline-flex items-center gap-2 text-sm text-[#1C2220] group-hover:text-[#C56A4E] transition-colors"
                  >
                    Read full bio
                    <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  </span>
                )}
              </motion.div>
            );

            return m.to ? (
              <Link key={m.name} to={m.to} className="block h-full">
                {Card}
              </Link>
            ) : (
              <div key={m.name} className="h-full">
                {Card}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Team;
